import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../supabase";
import { useAuth } from "../context/AuthContext";
import { useSite } from "../context/SiteContext";
import toast from "react-hot-toast";
import {
  Save,
  ArrowLeft,
  Mail,
  User,
  Briefcase,
  Loader2,
  Fingerprint,
  RefreshCcw,
  ShieldCheck,
  Layers,
  Check,
  Lock,
  Unlock,
  Ban,
  Store,
  MapPin,
} from "lucide-react";
import { ALL_MODULES } from "../utils/organizationModules";
import { MODULE_ORG_ALIAS_MAP } from "../utils/accessModules";
import { buildEffectiveModulePermissions } from "../utils/userModulePermissions";
import { normalizeAllowedSiteIds, sanitizeAllowedSiteIds } from "../utils/siteAccess";

const ROLES = ["cajero", "vendedor", "operario", "encargado", "admin", "administrador"];

// Módulos (mantené este catálogo alineado con sidebar/rutas)
const TODAS_LAS_FUNCIONES = [
  {
    group: "Ventas",
    items: [
      { id: "ventas-pos", label: "Punto de Venta" },
      { id: "ventas-historial", label: "Historial" },
      { id: "clientes", label: "Clientes" },
    ],
  },
  {
    group: "Stock",
    items: [
      { id: "productos", label: "Productos" },
      { id: "inventario", label: "Inventario" },
      { id: "produccion", label: "Producción" },
    ],
  },
  {
    group: "Finanzas",
    items: [
      { id: "tesoreria", label: "Tesorería" },
      { id: "caja", label: "Caja" },
      { id: "gastos", label: "Gastos" },
      { id: "flujo-fondos", label: "Flujo de Fondos" },
    ],
  },
  {
    group: "Sistema",
    items: [
      { id: "agenda", label: "Agenda" },
      { id: "chatbot", label: "Chatbot" },
      { id: "configuracion", label: "Configuración" },
      { id: "permisos", label: "Permisos" },
    ],
  },
];
const CATEGORY_ORDER = ["Principal", "Ventas", "CRM", "Community Manager", "Digital", "Logistica", "Compras", "Inventario", "Produccion", "Finanzas", "Sistema", "Configuracion"];

/**
 * Overrides reales:
 * - role_templates: plantilla base por rol (allowed)
 * - role_permissions: overrides por usuario (ALLOW o DENY) usando is_allowed boolean
 *
 * Estado UI por módulo:
 * - override = null   -> hereda (sin fila en role_permissions)
 * - override = true   -> force allow (role_permissions.is_allowed=true)
 * - override = false  -> force deny  (role_permissions.is_allowed=false)
 */

function UsuarioDetalle({ user: passedUser, onBack, onNavigate }) {
  const { profile: adminProfile, refreshProfile } = useAuth();
  const { refreshSites } = useSite();
  const { id: routeId } = useParams();
  const canManageUsers = ["admin", "administrador"].includes(String(adminProfile?.role || "").toLowerCase());

  const [localUser, setLocalUser] = useState(passedUser || null);
  const user = localUser;

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState(passedUser || {});

  // Plantilla base del rol (role_templates)
  const [basePerms, setBasePerms] = useState(new Set());

  // Overrides del usuario (role_permissions): Map<module_id, boolean> (true allow, false deny)
  const [overrides, setOverrides] = useState(new Map());

  const [orgModules, setOrgModules] = useState(null);
  const [organizationSites, setOrganizationSites] = useState([]);
  const [siteAccessMode, setSiteAccessMode] = useState("all");
  const [allowedSiteIds, setAllowedSiteIds] = useState([]);

  useEffect(() => {
    if (user) {
      setFormData({ ...user });
      const nextAllowedSiteIds = normalizeAllowedSiteIds(user.allowed_site_ids);
      setAllowedSiteIds(nextAllowedSiteIds);
      setSiteAccessMode(nextAllowedSiteIds.length > 0 ? "custom" : "all");
    }
  }, [user]);

  useEffect(() => {
    setAllowedSiteIds((current) => sanitizeAllowedSiteIds(current, organizationSites));
  }, [organizationSites]);

  useEffect(() => {
    const fetchUser = async () => {
      if (passedUser) {
        setLocalUser(passedUser);
        if (passedUser.id) setLoading(false);
        return;
      }
      if (routeId) {
        setLoading(true);
        const { data, error } = await supabase.from('profiles').select('*').eq('id', routeId).single();
        if (data) {
          setLocalUser(data);
          setFormData(data);
        } else {
          toast.error("Usuario no encontrado");
        }
      }
    };
    fetchUser();
  }, [passedUser, routeId]);

  useEffect(() => {
    if (user?.id) {
      fetchAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Si cambia el rol en el select, refrescamos plantilla base (y mantenemos overrides del usuario,
  // porque son "excepciones" que el admin podría querer conservar o limpiar)
  useEffect(() => {
    if (adminProfile?.organization_id && formData?.role) {
      fetchBaseRolePermissions(formData.role);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminProfile?.organization_id, formData?.role]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchBaseRolePermissions(formData.role || user.role),
        fetchUserOverrides(),
        fetchOrganizationModules(),
        canManageUsers ? fetchOrganizationSites() : Promise.resolve(),
      ]);
    } catch (e) {
      // errores ya toasteados
    } finally {
      setLoading(false);
    }
  };

  const fetchBaseRolePermissions = async (roleName) => {
    try {
      if (!adminProfile?.organization_id) return;

      const { data, error } = await supabase
        .from("role_templates")
        .select("module_id")
        .eq("organization_id", adminProfile.organization_id)
        .eq("role_name", roleName || "cajero");

      if (error) throw error;

      setBasePerms(new Set((data || []).map((d) => d.module_id)));
    } catch (err) {
      console.error(err);
      toast.error("Error al cargar plantilla del rol");
      setBasePerms(new Set());
      throw err;
    }
  };

  const fetchUserOverrides = async () => {
    try {
      const { data, error } = await supabase
        .from("role_permissions")
        .select("module_id,is_allowed")
        .eq("user_id", user.id)
        .eq("organization_id", adminProfile.organization_id);

      if (error) throw error;

      const m = new Map();
      (data || []).forEach((r) => m.set(r.module_id, Boolean(r.is_allowed)));
      setOverrides(m);
    } catch (err) {
      console.error(err);
      toast.error("Error al cargar overrides del usuario");
      setOverrides(new Map());
      throw err;
    }
  };

  const fetchOrganizationModules = async () => {
    try {
      if (!adminProfile?.organization_id) return;

      const { data, error } = await supabase
        .from("organizations")
        .select("custom_modules")
        .eq("id", adminProfile.organization_id)
        .single();

      if (error) throw error;

      setOrgModules(Array.isArray(data?.custom_modules) ? data.custom_modules : null);
    } catch (err) {
      console.error(err);
      setOrgModules(null);
    }
  };

  const fetchOrganizationSites = async () => {
    try {
      if (!adminProfile?.organization_id) return;

      const rpcResponse = await supabase.rpc("list_all_org_sites_for_user_management", {
        target_org: adminProfile.organization_id,
      });

      let nextSites = rpcResponse.data;
      let nextError = rpcResponse.error;

      if (nextError) {
        const fallbackResponse = await supabase
          .from("sitios")
          .select("id,nombre,direccion,is_active")
          .eq("organization_id", adminProfile.organization_id)
          .order("nombre", { ascending: true });

        nextSites = fallbackResponse.data;
        nextError = fallbackResponse.error;
      }

      if (nextError) throw nextError;

      setOrganizationSites(
        (Array.isArray(nextSites) ? nextSites : []).filter((siteRow) => siteRow?.is_active !== false)
      );
    } catch (err) {
      console.error(err);
      toast.error("Error al cargar sitios de la organizacion");
      setOrganizationSites([]);
      throw err;
    }
  };

  // Permisos efectivos = base + overrides (allow agrega, deny quita)
  const effectivePerms = useMemo(() => {
    // Mismo calculo que el runtime (Sistema/Sidebar), incluidos los alias
    // heredados del CRM: asi el badge "efectivo" muestra lo que el usuario ve.
    return buildEffectiveModulePermissions({
      baseRows: [...basePerms].map((module_id) => ({ module_id })),
      overrideRows: [...overrides.entries()].map(([module_id, is_allowed]) => ({ module_id, is_allowed })),
    });
  }, [basePerms, overrides]);

  const availableModules = useMemo(() => {
    const orgModulesSet = Array.isArray(orgModules) ? new Set(orgModules) : null;

    return ALL_MODULES.filter((moduleRow) => {
      if (!orgModulesSet) return true;
      const candidateIds = [moduleRow.id, ...(MODULE_ORG_ALIAS_MAP[moduleRow.id] || [])];
      return candidateIds.some((candidateId) => orgModulesSet.has(candidateId));
    });
  }, [orgModules]);

  const groupedModules = useMemo(() => {
    const categoryMap = availableModules.reduce((accumulator, moduleRow) => {
      const group = moduleRow.category || "Otros";
      if (!accumulator[group]) accumulator[group] = [];
      accumulator[group].push({ id: moduleRow.id, label: moduleRow.label, group });
      return accumulator;
    }, {});

    return Object.entries(categoryMap)
      .sort((left, right) => {
        const leftIndex = CATEGORY_ORDER.indexOf(left[0]);
        const rightIndex = CATEGORY_ORDER.indexOf(right[0]);
        const safeLeft = leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex;
        const safeRight = rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex;
        if (safeLeft !== safeRight) return safeLeft - safeRight;
        return left[0].localeCompare(right[0]);
      })
      .map(([group, items]) => ({
        group,
        items: [...items].sort((left, right) => left.label.localeCompare(right.label)),
      }));
  }, [availableModules]);

  const allModulesFlat = useMemo(() => {
    return groupedModules.flatMap((g) => g.items.map((it) => ({ ...it, group: g.group })));
  }, [groupedModules]);

  const kpis = useMemo(() => {
    const totalModules = allModulesFlat.length;
    const effectiveCount = effectivePerms.size;
    const overridesCount = overrides.size;
    const role = (formData.role || "usuario").toUpperCase();
    return { totalModules, effectiveCount, overridesCount, role };
  }, [allModulesFlat.length, effectivePerms.size, overrides.size, formData.role]);

  const sanitizedAllowedSiteIds = useMemo(
    () => sanitizeAllowedSiteIds(allowedSiteIds, organizationSites),
    [allowedSiteIds, organizationSites]
  );

  const selectedAllowedSiteSet = useMemo(
    () => new Set(sanitizedAllowedSiteIds),
    [sanitizedAllowedSiteIds]
  );

  const siteVisibilityCount = siteAccessMode === "all"
    ? organizationSites.length
    : sanitizedAllowedSiteIds.length;

  // 3 estados por click: inherit -> force allow -> force deny -> inherit
  const cycleOverride = (moduleId) => {
    if (!canManageUsers) return;
    setOverrides((prev) => {
      const next = new Map(prev);
      const current = next.has(moduleId) ? next.get(moduleId) : null;

      // current: null (inherit), true, false
      if (current === null) next.set(moduleId, true);
      else if (current === true) next.set(moduleId, false);
      else next.delete(moduleId);

      return next;
    });
  };

  const toggleAllowedSite = (siteId) => {
    if (!canManageUsers) return;

    setAllowedSiteIds((prev) => {
      const next = new Set(sanitizeAllowedSiteIds(prev, organizationSites));
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return Array.from(next);
    });
  };

  const handleUpdate = async (e) => {
    e?.preventDefault?.();
    if (!canManageUsers) {
      toast.error("Solo un administrador puede modificar roles y modulos de usuarios.");
      return;
    }

    if (siteAccessMode === "custom" && sanitizedAllowedSiteIds.length === 0) {
      toast.error("Selecciona al menos 1 sitio o habilita acceso a todos.");
      return;
    }

    setIsSaving(true);

    try {
      // VALIDACION DE ADMIN UNICO
      if ((user.role === 'admin' || user.role === 'administrador') && (formData.role !== 'admin' && formData.role !== 'administrador')) {
        const { data: admins } = await supabase
          .from('profiles')
          .select('id')
          .eq('organization_id', adminProfile.organization_id)
          .in('role', ['admin', 'administrador']);
        
        if (admins && admins.length <= 1) {
          setIsSaving(false);
          toast.error("Debe haber al menos 1 administrador en la empresa.");
          return;
        }
      }

      // 1) actualizar rol/nombre
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          full_name: formData.full_name,
          role: formData.role,
          allowed_site_ids: siteAccessMode === "all" ? null : sanitizedAllowedSiteIds,
        })
        .eq("id", user.id);

      if (profileError) throw profileError;

      // 2) reemplazar overrides del usuario (no plantillas)
      const { error: delErr } = await supabase
        .from("role_permissions")
        .delete()
        .eq("user_id", user.id)
        .eq("organization_id", adminProfile.organization_id);

      if (delErr) throw delErr;

      const rows = Array.from(overrides.entries()).map(([moduleId, isAllowed]) => ({
        organization_id: adminProfile.organization_id,
        user_id: user.id,
        role_name: formData.role, // informativo
        module_id: moduleId,
        is_allowed: Boolean(isAllowed),
      }));

      if (rows.length > 0) {
        const { error: insErr } = await supabase.from("role_permissions").insert(rows);
        if (insErr) throw insErr;
      }

      if (adminProfile?.id === user.id) {
        await refreshProfile?.();
        await refreshSites?.();
      }

      toast.success("Perfil sincronizado (rol + overrides)");
      if (onNavigate) onNavigate('permisos');
      else if (onBack) onBack();
    } catch (err) {
      console.error(err);
      toast.error("Error al guardar (ver consola)");
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin mb-2 text-[#51A7FD]" size={20} />
        <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest italic">
          Cargando perfil...
        </p>
      </div>
    );
  }

  return (
    <div className="w-full p-3 md:p-4 bg-[#F8FAFC] min-h-screen font-sans text-slate-700">
      {/* HEADER (Modelo Productos) */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 px-1">
        <div className="flex items-center gap-3">
          <button
            onClick={() => onNavigate ? onNavigate('permisos') : onBack && onBack()}
            className="p-2 bg-white border border-slate-200 rounded-lg text-slate-400 hover:text-[#51A7FD] transition-all shadow-sm"
            title="Volver"
            aria-label="Volver"
          >
            <ArrowLeft size={18} />
          </button>

          <div className="h-9 w-9 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-500">
            <Fingerprint size={16} />
          </div>

          <div>
            <h1 className="text-lg md:text-xl font-bold text-slate-800 uppercase tracking-tight">
              Usuario
            </h1>
            <p className="text-[9px] font-medium text-slate-400 uppercase tracking-widest mt-0.5 italic">
              Rol + Overrides · {formData?.email || "—"}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={fetchAll}
            className="bg-white border border-slate-200 text-slate-600 px-4 py-2.5 rounded-xl shadow-sm hover:bg-slate-50 transition-all flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest"
          >
            <RefreshCcw size={16} /> Sincronizar
          </button>

          <button
            type="button"
            onClick={handleUpdate}
            disabled={isSaving || !canManageUsers}
            className="bg-[#51A7FD] text-white px-6 py-2.5 rounded-xl shadow-md shadow-blue-100 hover:scale-[1.02] active:scale-95 transition-all flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest justify-center disabled:opacity-60"
          >
            {isSaving ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Guardando
              </>
            ) : (
              <>
                <Save size={16} /> Guardar
              </>
            )}
          </button>
        </div>
      </div>

      {/* KPI (Modelo Productos) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex items-center justify-between">
          <div>
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Rol</p>
            <p className="text-[14px] font-bold text-slate-800 tracking-tight mt-0.5">
              {kpis.role}
            </p>
          </div>
          <ShieldCheck size={18} className="text-amber-600" />
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex items-center justify-between">
          <div>
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">
              Permisos efectivos
            </p>
            <p className="text-[14px] font-bold text-slate-800 tracking-tight mt-0.5">
              {kpis.effectiveCount}
            </p>
          </div>
          <Check size={18} className="text-emerald-600" />
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex items-center justify-between">
          <div>
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">
              Overrides (excepciones)
            </p>
            <p className="text-[14px] font-bold text-slate-800 tracking-tight mt-0.5">
              {kpis.overridesCount}
            </p>
          </div>
          <Layers size={18} className="text-[#51A7FD]" />
        </div>
      </div>

      <form onSubmit={handleUpdate} className="space-y-4">
        {!canManageUsers ? (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-2xl px-4 py-3 text-[11px] font-bold">
            Esta pantalla queda en solo lectura. Solo un usuario admin puede cambiar roles y modulos visibles.
          </div>
        ) : null}

        {/* Identidad */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <label className="text-[8px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                Nombre completo
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                <input
                  required
                  value={formData.full_name || ""}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  disabled={!canManageUsers}
                  className="w-full bg-slate-50 border border-slate-100 pl-9 pr-3 py-2 rounded-lg text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[8px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                <input
                  disabled
                  value={formData.email || ""}
                  className="w-full bg-slate-100 border border-slate-200 pl-9 pr-3 py-2 rounded-lg text-[10px] font-bold lowercase text-slate-400 outline-none cursor-not-allowed"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[8px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                Rol
              </label>
              <div className="relative">
                <Briefcase
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300"
                  size={14}
                />
                <select
                  value={formData.role || "cajero"}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  disabled={!canManageUsers}
                  className="w-full bg-slate-50 border border-slate-100 pl-9 pr-3 py-2 rounded-lg text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner appearance-none cursor-pointer"
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {canManageUsers ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-500 border border-slate-100">
                  <Store size={14} />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-700 uppercase tracking-tight">
                    Visibilidad de sitios
                  </p>
                  <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest italic">
                    Define si este usuario puede ver todos los sitios o solo una selección.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setSiteAccessMode("all")}
                  disabled={!canManageUsers}
                  className={`px-3 py-2 rounded-xl border text-[9px] font-bold uppercase tracking-widest transition-all ${
                    siteAccessMode === "all"
                      ? "bg-[#0B2A36] text-white border-[#0B2A36]"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  } disabled:opacity-60`}
                >
                  Todos los sitios
                </button>
                <button
                  type="button"
                  onClick={() => setSiteAccessMode("custom")}
                  disabled={!canManageUsers}
                  className={`px-3 py-2 rounded-xl border text-[9px] font-bold uppercase tracking-widest transition-all ${
                    siteAccessMode === "custom"
                      ? "bg-[#51A7FD] text-white border-[#51A7FD]"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  } disabled:opacity-60`}
                >
                  Selección manual
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div className="bg-slate-50 rounded-xl border border-slate-100 px-4 py-3">
                <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Modo</p>
                <p className="text-[12px] font-bold text-slate-800 uppercase mt-1">
                  {siteAccessMode === "all" ? "Todos" : "Restringido"}
                </p>
              </div>
              <div className="bg-slate-50 rounded-xl border border-slate-100 px-4 py-3">
                <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Sitios visibles</p>
                <p className="text-[12px] font-bold text-slate-800 uppercase mt-1">
                  {siteVisibilityCount || 0}
                </p>
              </div>
              <div className="bg-slate-50 rounded-xl border border-slate-100 px-4 py-3">
                <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Sitios activos</p>
                <p className="text-[12px] font-bold text-slate-800 uppercase mt-1">
                  {organizationSites.length || 0}
                </p>
              </div>
            </div>

            {siteAccessMode === "custom" ? (
              organizationSites.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {organizationSites.map((siteRow) => {
                    const isSelected = selectedAllowedSiteSet.has(String(siteRow.id));

                    return (
                      <button
                        key={siteRow.id}
                        type="button"
                        onClick={() => toggleAllowedSite(String(siteRow.id))}
                        disabled={!canManageUsers}
                        className={`w-full text-left rounded-xl border px-4 py-3 transition-all ${
                          isSelected
                            ? "bg-emerald-50 border-emerald-200 shadow-sm"
                            : "bg-white border-slate-200 hover:bg-slate-50"
                        } disabled:opacity-60`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className={`text-[10px] font-bold uppercase tracking-tight ${isSelected ? "text-emerald-800" : "text-slate-700"}`}>
                              {siteRow.nombre}
                            </p>
                            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-1 flex items-center gap-1">
                              <MapPin size={10} />
                              <span className="truncate">{siteRow.direccion || "Sin dirección"}</span>
                            </p>
                          </div>

                          <span className={`p-1.5 rounded-lg ${isSelected ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400"}`}>
                            <Check size={12} />
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-5 text-center">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                    No hay sitios activos para asignar.
                  </p>
                </div>
              )
            ) : (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-5">
                <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">
                  Este usuario podrá operar y visualizar todos los sitios activos de la organización.
                </p>
              </div>
            )}
          </div>
        ) : null}

        {/* Permisos */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-500 border border-slate-100">
              <Fingerprint size={14} />
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-700 uppercase tracking-tight">
                Permisos efectivos (rol + overrides)
              </p>
              <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest italic">
                Click para alternar: Hereda → Allow → Deny → Hereda
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {groupedModules.map((group) => (
              <div key={group.group} className="space-y-2">
                <h3 className="text-[8px] font-bold text-slate-400 uppercase tracking-widest border-l-2 border-slate-100 pl-2">
                  {group.group}
                </h3>

                <div className="space-y-1">
                  {group.items.map((item) => {
                    const inheritedAllowed = basePerms.has(item.id);
                    const hasOverride = overrides.has(item.id);
                    const overrideValue = hasOverride ? overrides.get(item.id) : null; // true/false/null

                    // estado visual
                    const effectiveAllowed = effectivePerms.has(item.id);

                    let badgeClass = "bg-slate-50/30 border-transparent hover:bg-slate-50";
                    let icon = inheritedAllowed ? <Unlock size={12} /> : <Lock size={12} />;
                    let iconWrap = inheritedAllowed ? "bg-slate-200 text-white" : "bg-slate-200 text-white";

                    if (!hasOverride) {
                      // hereda
                      badgeClass = inheritedAllowed
                        ? "bg-slate-50 border-slate-100 hover:bg-slate-50/70"
                        : "bg-slate-50/30 border-transparent hover:bg-slate-50";
                      iconWrap = inheritedAllowed ? "bg-slate-200 text-white" : "bg-slate-200 text-white";
                      icon = inheritedAllowed ? <Unlock size={12} /> : <Lock size={12} />;
                    } else if (overrideValue === true) {
                      // force allow
                      badgeClass = "bg-emerald-50/60 border-emerald-100 hover:bg-emerald-50";
                      iconWrap = "bg-[#3FF2AA] text-[#0B2A36]";
                      icon = <Check size={12} />;
                    } else {
                      // force deny
                      badgeClass = "bg-rose-50/60 border-rose-100 hover:bg-rose-50";
                      iconWrap = "bg-rose-500 text-white";
                      icon = <Ban size={12} />;
                    }

                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => cycleOverride(item.id)}
                        disabled={!canManageUsers}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-xl transition-all border ${badgeClass} disabled:cursor-not-allowed disabled:opacity-70`}
                        title={`${item.id} · efectivo: ${effectiveAllowed ? "ALLOW" : "DENY"}`}
                      >
                        <span
                          className={`text-[9px] font-bold uppercase tracking-tight ${
                            effectiveAllowed ? "text-slate-800" : "text-slate-500"
                          }`}
                        >
                          {item.label}
                        </span>

                        <span className={`p-1.5 rounded-lg transition-all ${iconWrap}`}>
                          {icon}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

          </div>
        </div>

        <div className="h-10" />
      </form>
    </div>
  );
}

export default UsuarioDetalle;
